import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function ForumThread() {
  const { id } = useParams();
  const { user } = useAuth();
  const [thread, setThread] = useState(null);
  const [posts, setPosts] = useState([]);
  const [reply, setReply] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/forum/threads/${id}`);
      setThread(data.thread);
      setPosts(data.posts);
    } catch {
      setThread(false);
    }
  }, [id]);

  useEffect(() => { window.scrollTo(0, 0); load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!user) return toast.error("Please sign in to reply.");
    try {
      await api.post(`/forum/threads/${id}/posts`, { body: reply });
      setReply("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (thread === false) return <div className="flex min-h-screen items-center justify-center text-white/40">Thread not found.</div>;
  if (!thread) return <div className="flex min-h-screen items-center justify-center text-white/40">Loading…</div>;

  return (
    <div className="pt-28 md:pt-36" data-testid="forum-thread-page">
      <div className="mx-auto max-w-3xl px-6 md:px-12">
        <Link to="/forum" className="mb-8 inline-flex items-center gap-2 text-xs uppercase tracking-luxe text-white/50 transition-colors hover:text-velvet-gold">
          <ArrowLeft size={14} /> The Lounge
        </Link>
        <span className="text-[10px] uppercase tracking-luxe text-velvet-gold">{thread.category}</span>
        <h1 className="mt-2 font-heading text-4xl font-semibold leading-tight tracking-tight text-white md:text-5xl">{thread.title}</h1>
        <p className="mt-3 text-xs text-white/40">by {thread.author_name}</p>
        <p className="mt-6 text-base font-light leading-relaxed text-white/70">{thread.body}</p>

        <div className="mt-12 space-y-6" data-testid="forum-posts">
          <p className="text-xs uppercase tracking-luxe text-velvet-gold">{posts.length} replies</p>
          {posts.map((p) => (
            <div key={p.id} className="border-t border-white/10 pt-6">
              <div className="flex items-center justify-between">
                <span className="text-sm text-white">{p.author_name}</span>
              </div>
              <p className="mt-2 text-sm font-light leading-relaxed text-white/60">{p.body}</p>
            </div>
          ))}
        </div>

        {user ? (
          <form onSubmit={submit} className="mt-10 border-t border-white/10 pt-8" data-testid="forum-reply-form">
            <textarea required rows={3} placeholder="Add your reply…" value={reply} data-testid="forum-reply-input" onChange={(e) => setReply(e.target.value)}
              className="w-full border-b border-white/15 bg-transparent py-3 text-sm text-white placeholder:text-white/30 focus:border-velvet-gold focus:outline-none" />
            <button type="submit" data-testid="forum-reply-submit" className="mt-4 rounded-full border border-white/20 px-6 py-3 text-xs uppercase tracking-luxe text-white transition-colors hover:bg-white hover:text-black">Reply</button>
          </form>
        ) : (
          <p className="mt-10 border-t border-white/10 pt-8 text-sm font-light text-white/40"><Link to="/login" className="text-velvet-gold">Sign in</Link> to join the conversation.</p>
        )}
      </div>
    </div>
  );
}
